/**
 * Display copy for SE-5523's suggestion reason categories.
 *
 * The backend states exactly THREE categories. SE-5523 is explicit on two
 * points that this module encodes:
 *
 * 1. **An unsupported category is OMITTED, never replaced by a fallback.**
 *    There is deliberately no "Other"/"Recommended" catch-all here. If the
 *    backend ever adds a fourth category, the chip for it simply does not
 *    render until this map gains an entry with real product copy — a silent
 *    omission is correct, an invented label is not.
 * 2. **GPR is never exposed as a stated reason.** GPR participates in ranking
 *    (`bestRank`) but is not a category, so no copy below may cite it.
 * @module features/player-kanban/constants/suggestionReasons
 */

import { SuggestionReasonCategory } from "@/generated/graphql";

/** Chip label plus the full explanation behind it. */
export interface SuggestionReasonCopy {
  /** Compact chip label. Kept short — the chip row must stay scannable. */
  readonly label: string;
  /**
   * The full explanation. Surfaced on hover on web, on press everywhere, and
   * as the chip's accessibility label so it is never hover-only.
   */
  readonly explanation: string;
}

/**
 * Category → copy. A partial record on purpose: a category absent from this
 * map renders NO chip rather than a generic one.
 */
export const SUGGESTION_REASON_COPY: Partial<
  Record<SuggestionReasonCategory, SuggestionReasonCopy>
> = {
  [SuggestionReasonCategory.SquadDepth]: {
    label: "Squad depth",
    explanation:
      "Your squad is thin in this player's position group, so adding cover here closes a depth gap.",
  },
  [SuggestionReasonCategory.UnderperformingPlayers]: {
    label: "Underperforming",
    explanation:
      "Players currently filling this player's role are underperforming, making them an upgrade candidate.",
  },
  [SuggestionReasonCategory.YoungPlayers]: {
    label: "Young talent",
    explanation:
      "This player fits your pipeline's young-talent profile and has development runway ahead.",
  },
};

/**
 * Resolve the renderable reasons for a candidate, dropping any category this
 * client has no copy for.
 * @param reasons - Categories as returned by the backend. May contain more
 * than one entry, and may contain categories added after this build shipped.
 * @returns The categories that have copy, paired with it, in backend order.
 */
export const resolveSuggestionReasons = (
  reasons: readonly SuggestionReasonCategory[]
): readonly (SuggestionReasonCopy & {
  readonly category: SuggestionReasonCategory;
})[] =>
  reasons.flatMap(category => {
    const copy = SUGGESTION_REASON_COPY[category];
    return copy === undefined ? [] : [{ category, ...copy }];
  });
